import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Activity,
  RefreshCw,
  CheckCircle,
  AlertTriangle,
  XCircle,
  Database,
  Bike,
  UtensilsCrossed,
  Loader2,
  Play,
  BarChart3
} from "lucide-react";

export function DataHealthCheck() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/debug/service-type-stats`);
      setStats(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to fetch data health stats");
      toast.error("Failed to fetch data health stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const runMigration = async () => {
    setMigrating(true);
    try {
      const response = await axios.post(`${API}/admin/migrate/fix-qc-rides`);
      const result = response.data;
      
      if (result.updated_count > 0) {
        toast.success(`Migration complete! Fixed ${result.updated_count} QC rides`);
      } else {
        toast.info("No rides needed migration - data is already clean");
      }
      
      // Refresh stats
      fetchStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Migration failed");
    } finally {
      setMigrating(false);
    }
  };

  // Calculate health status
  const getHealthStatus = () => {
    if (!stats) return { status: "unknown", message: "Loading..." };
    
    const { total_rides, bike_taxi_rides, food_delivery_rides, breakdown } = stats;
    
    // Check if totals match
    if (bike_taxi_rides + food_delivery_rides !== total_rides) {
      return { 
        status: "warning", 
        message: `Data mismatch: ${bike_taxi_rides} + ${food_delivery_rides} ≠ ${total_rides}` 
      };
    }
    
    // Check for QC rides without proper service_type
    const qcWithoutServiceType = breakdown?.find(
      b => b.is_qc_ride && b.service_type === "NOT_SET"
    );
    
    if (qcWithoutServiceType && qcWithoutServiceType.total_rides > 0) {
      return { 
        status: "error", 
        message: `${qcWithoutServiceType.total_rides} QC rides missing service_type` 
      };
    }
    
    return { status: "healthy", message: "All data properly separated" };
  };

  const healthStatus = getHealthStatus();

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-600">
              <XCircle className="w-6 h-6" />
              <span>{error}</span>
            </div>
            <Button onClick={fetchStats} className="mt-4" variant="outline">
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Activity className="w-7 h-7 text-amber-500" />
            Data Health Check
          </h1>
          <p className="text-gray-500 mt-1">
            Monitor and fix data separation between Bike Taxi and Food Delivery
          </p>
        </div>
        <Button onClick={fetchStats} variant="outline" disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Health Status Banner */}
      <Card className={`border-2 ${
        healthStatus.status === "healthy" ? "border-green-300 bg-green-50" :
        healthStatus.status === "warning" ? "border-yellow-300 bg-yellow-50" :
        healthStatus.status === "error" ? "border-red-300 bg-red-50" :
        "border-gray-300 bg-gray-50"
      }`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {healthStatus.status === "healthy" ? (
                <CheckCircle className="w-10 h-10 text-green-500" />
              ) : healthStatus.status === "warning" ? (
                <AlertTriangle className="w-10 h-10 text-yellow-500" />
              ) : healthStatus.status === "error" ? (
                <XCircle className="w-10 h-10 text-red-500" />
              ) : (
                <Database className="w-10 h-10 text-gray-500" />
              )}
              <div>
                <h2 className={`text-xl font-semibold ${
                  healthStatus.status === "healthy" ? "text-green-700" :
                  healthStatus.status === "warning" ? "text-yellow-700" :
                  healthStatus.status === "error" ? "text-red-700" :
                  "text-gray-700"
                }`}>
                  {healthStatus.status === "healthy" ? "Data Healthy" :
                   healthStatus.status === "warning" ? "Warning" :
                   healthStatus.status === "error" ? "Action Required" :
                   "Unknown Status"}
                </h2>
                <p className={`${
                  healthStatus.status === "healthy" ? "text-green-600" :
                  healthStatus.status === "warning" ? "text-yellow-600" :
                  healthStatus.status === "error" ? "text-red-600" :
                  "text-gray-600"
                }`}>
                  {healthStatus.message}
                </p>
              </div>
            </div>
            
            {healthStatus.status === "error" && (
              <Button 
                onClick={runMigration} 
                disabled={migrating}
                className="bg-red-600 hover:bg-red-700"
              >
                {migrating ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Play className="w-4 h-4 mr-2" />
                )}
                Run Migration Fix
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Total Rides */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Database className="w-4 h-4" />
              Total Rides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-gray-900">
              {stats?.total_rides || 0}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              All rides in database
            </p>
          </CardContent>
        </Card>

        {/* Bike Taxi Rides */}
        <Card className="border-blue-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-blue-600 flex items-center gap-2">
              <Bike className="w-4 h-4" />
              Bike Taxi Rides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">
              {stats?.bike_taxi_rides || 0}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {stats?.total_rides > 0 
                ? `${((stats.bike_taxi_rides / stats.total_rides) * 100).toFixed(1)}% of total`
                : "No rides yet"
              }
            </p>
          </CardContent>
        </Card>

        {/* Food Delivery Rides */}
        <Card className="border-orange-200">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-orange-600 flex items-center gap-2">
              <UtensilsCrossed className="w-4 h-4" />
              Food Delivery Rides
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">
              {stats?.food_delivery_rides || 0}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {stats?.total_rides > 0 
                ? `${((stats.food_delivery_rides / stats.total_rides) * 100).toFixed(1)}% of total`
                : "No rides yet"
              }
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Detailed Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-amber-500" />
            Service Type Breakdown
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Service Type</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-500">Is QC Ride</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Total Rides</th>
                  <th className="text-right py-3 px-4 font-medium text-gray-500">Completed</th>
                  <th className="text-center py-3 px-4 font-medium text-gray-500">Status</th>
                </tr>
              </thead>
              <tbody>
                {stats?.breakdown?.map((item, index) => {
                  // Determine if this is a problem row
                  const isProblem = item.is_qc_ride && item.service_type === "NOT_SET";
                  
                  return (
                    <tr 
                      key={index} 
                      className={`border-b last:border-0 ${
                        isProblem ? "bg-red-50" : "hover:bg-gray-50"
                      }`}
                    >
                      <td className="py-3 px-4">
                        <span className={`inline-flex items-center gap-2 ${
                          item.service_type === "food_delivery" ? "text-orange-600" :
                          item.service_type === "bike_taxi" ? "text-blue-600" :
                          item.service_type === "NOT_SET" ? "text-gray-400 italic" :
                          "text-gray-600"
                        }`}>
                          {item.service_type === "food_delivery" && <UtensilsCrossed className="w-4 h-4" />}
                          {item.service_type === "bike_taxi" && <Bike className="w-4 h-4" />}
                          {item.service_type === "NOT_SET" && <AlertTriangle className="w-4 h-4" />}
                          {item.service_type || "NOT_SET"}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        {item.is_qc_ride ? (
                          <span className="inline-flex items-center gap-1 text-orange-600">
                            <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            Yes (QC-)
                          </span>
                        ) : (
                          <span className="text-gray-400">No</span>
                        )}
                      </td>
                      <td className="py-3 px-4 text-right font-medium">
                        {item.total_rides}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500">
                        {item.completed_rides}
                      </td>
                      <td className="py-3 px-4 text-center">
                        {isProblem ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                            <XCircle className="w-3 h-3" />
                            Needs Fix
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                            <CheckCircle className="w-3 h-3" />
                            OK
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {(!stats?.breakdown || stats.breakdown.length === 0) && (
                  <tr>
                    <td colSpan={5} className="py-8 text-center text-gray-500">
                      No data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Info Section */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="p-6">
          <h3 className="font-semibold text-blue-800 mb-2">What is Data Health Check?</h3>
          <div className="text-sm text-blue-700 space-y-2">
            <p>
              This dashboard monitors the separation between <strong>Bike Taxi</strong> and <strong>Food Delivery</strong> data 
              to ensure accurate reporting and financial calculations.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Bike Taxi rides</strong>: Regular bike taxi bookings (no QC- prefix)</li>
              <li><strong>Food Delivery rides</strong>: Quick Commerce orders (QC- prefix, service_type = "food_delivery")</li>
              <li><strong>Migration Fix</strong>: Automatically sets service_type for historical QC rides that are missing it</li>
            </ul>
            <p className="mt-3 text-blue-600">
              If you see "Needs Fix" status, click "Run Migration Fix" to automatically correct the data.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
